import * as React from 'react'
import cn from 'classnames'
import { ImageUrl, Args, withArgs } from '@utils'
import {
  responsiveAttrs,
  SrcSizes,
  SrcType,
  BaseType,
} from '../../utils/imageUtils'
import useIntersectionObserver from '../../utils/useInteractionObserver'
import * as st from './responsiveImage.module.scss'

type ResponsiveImage = {
  src?: string
  alt: string
  baseValue?: number
  baseType?: BaseType
  srcSizes?: SrcSizes
  format?: SrcType
  skipStripTags?: boolean
  isLazy?: boolean
  isAtfMainImage?: boolean
  className?: string
}

const INTERSECTION_OBSERVER_ROOT_MARGIN = '200px'
const THUMBNAIL_WIDTH = 50

const ResponsiveLazyImage = (props: ResponsiveImage & Args) => {
  const {
    src,
    baseValue,
    format,
    baseType,
    srcSizes,
    skipStripTags,
    imageLoadTreatment,
    className,
    classNameContainer,
    alt,
  } = props
  const eltRef = React.useRef()
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [isVisible, setVisible] = React.useState(false)
  React.useEffect(() => {
    if (['T2', 'T3'].includes(imageLoadTreatment)) {
      setVisible(true)
    }
  }, [])
  useIntersectionObserver({
    enabled: imageLoadTreatment === 'T4',
    target: eltRef,
    rootMargin: INTERSECTION_OBSERVER_ROOT_MARGIN,
    onIntersect: ([{ isIntersecting }], observerElement) => {
      if (isIntersecting) {
        setVisible(true)
        observerElement.unobserve(eltRef.current)
      }
    },
  })

  const showThumbnail = imageLoadTreatment === 'T2'
  return (
    <>
      <noscript>
        <img src={src} className={className} alt={alt} />
        <style>{`.${st.imageContainer}{display:none;}`}</style>
      </noscript>
      <div ref={eltRef} className={cn(st.imageContainer, classNameContainer)}>
        {showThumbnail && (
          <img
            className={cn(st.thumb, { [st.hideThumb]: isLoaded })}
            src={new ImageUrl(src).scaleWidth(THUMBNAIL_WIDTH).getImageUrl()}
          />
        )}
        {isVisible && (
          <img
            {...responsiveAttrs({
              src,
              baseValue,
              baseType,
              srcSizes,
              format,
              skipStripTags,
            })}
            loading='lazy'
            onLoad={() => {
              setIsLoaded(true)
            }}
            alt={alt}
            className={cn(st.full, className, { [st.showLoaded]: isLoaded })}
          />
        )}
      </div>
    </>
  )
}

// C : Load all pictures
//
// T1 : Native Lazy loading for all images
//
// T2 : 1) Before hydration: Load thumbnails ATF/BTF/Additional
//      2) show full ATF when loaded
//      2) After hydration: load BTF/Additional images with native lazy loading
//
// T3 : 1) Before hydration: load thumbnail ATF only
//      2) show full ATF when loaded
//      3) After hydration, load BTF/Additional images with native lazy loading
//
// T4 : 1) Before hydration: load thumbnail ATF only
//      2) show full ATF when loaded
//      3) After hydration, load BTF/Additional images on intersection (very lazy)
export const ResponsiveImageWithArgs = (props: ResponsiveImage & Args) => {
  const {
    src,
    baseValue,
    format,
    baseType,
    srcSizes,
    skipStripTags,
    isLazy = false,
    imageLoadTreatment,
    className,
    classNameContainer,
    alt,
    isAtfMainImage,
  } = props

  if (!(src && baseValue)) {
    return null
  }

  if (['T2', 'T3', 'T4'].includes(imageLoadTreatment) && isAtfMainImage) {
    const { src: computedSrc, srcSet, sizes } = responsiveAttrs({
      src,
      baseValue,
      baseType,
      srcSizes,
      format,
      skipStripTags,
    })

    const thumbSrc = new ImageUrl(src).scaleWidth(50).getImageUrl()
    const BODY_CLASS = 'atfimgloaded'
    return (
      <>
        <noscript>
          <img src={src} className={className} alt={alt} />
          <style>{`.${st.imageContainer}{display:none;}`}</style>
        </noscript>
        {/* Using dangerouslySetInnerHTML to be able to run img onload before hydration */}
        <div
          className={`${st.imageContainer} ${classNameContainer}`}
          dangerouslySetInnerHTML={{
            __html: `
      <script>document.body.classList.remove("${BODY_CLASS}")</script>
      <img id="atf-thumb" class="${st.thumb}" src="${thumbSrc}"/>
      <img id="atf-full" class="${st.full} ${className}" src="${computedSrc}" srcSet="${srcSet}" sizes="${sizes}" alt="${alt}" onload="document.body.classList.add('${BODY_CLASS}')"/>
            `,
          }}
        />
      </>
    )
  }

  if (['T2', 'T3', 'T4'].includes(imageLoadTreatment) && isLazy) {
    return <ResponsiveLazyImage {...props} />
  }

  return (
    <img
      {...responsiveAttrs({
        src,
        baseValue,
        baseType,
        srcSizes,
        format,
        skipStripTags,
      })}
      loading={isLazy && imageLoadTreatment === 'T1' ? 'lazy' : undefined}
      className={className}
      alt={alt}
    />
  )
}

export const ResponsiveImage = withArgs(React.memo(ResponsiveImageWithArgs))
